// src/design-system/components/richTextEditor/extensions/ResizableMedia.ts
import React from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import {
  IMAGE_EXTENSIONS,
  VIDEO_EXTENSIONS,
  DOCUMENT_EXTENSIONS,
} from "@/utils/constants/mediaUploadConstants";
import type { MediaUploadResult } from "../types";

// Extend Commands interface
declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    resizableMedia: {
      setMedia: (options: {
        src: string;
        alt?: string;
        title?: string;
        thumbnailUrl?: string;
        width?: number | string;
        height?: number | string;
        mediaType?: "image" | "video" | "document";
      }) => ReturnType;
    };
  }
}

// Extended extensions for type checking
const imageExtensions: string[] = [...IMAGE_EXTENSIONS, "gif", "svg", "bmp", "ico", "tiff", "tif", "webp"];
const videoExtensions: string[] = [...VIDEO_EXTENSIONS, "mkv", "flv", "wmv"];
const documentExtensions: string[] = [...DOCUMENT_EXTENSIONS];
const DEFAULT_IMAGE_WIDTH = "420px";
const DEFAULT_DOCUMENT_WIDTH = "400px";
const inlinePlayableVideoExtensions = ["mp4", "3gp", "mov", "webm"];
const knownVideoHosts = [
  "youtube.com",
  "youtu.be",
  "m.youtube.com",
  "vimeo.com",
  "player.vimeo.com",
  "dailymotion.com",
  "twitch.tv",
];

const isKnownVideoUrl = (url: URL): boolean => {
  const host = url.hostname.replace(/^www\./, "");
  return knownVideoHosts.includes(host);
};

const isObjectStorageUrl = (url: URL): boolean => {
  const host = url.hostname.toLowerCase();
  return host.endsWith(".blob.core.windows.net");
};

const parseUrl = (src: string): URL | null => {
  try {
    const baseUrl =
      typeof window !== "undefined" ? window.location.origin : "https://notifyy.local";
    return new URL(src, baseUrl);
  } catch {
    return null;
  }
};

export const getVideoThumbnailForUrl = (src: string): string => {
  const url = parseUrl(src);
  if (!url) return "";

  const host = url.hostname.replace(/^www\./, "");
  if (host === "youtube.com" || host === "m.youtube.com") {
    const videoId =
      url.searchParams.get("v") ||
      url.pathname.match(/\/(?:embed|shorts)\/([^/?#]+)/)?.[1] ||
      "";
    return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : "";
  }

  if (host === "youtu.be") {
    const videoId = url.pathname.replace(/^\/+/, "").split("/")[0];
    return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : "";
  }

  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const videoId = url.pathname.split("/").filter(Boolean).pop();
    return videoId && /^\d+$/.test(videoId) ? `https://vumbnail.com/${videoId}.jpg` : "";
  }

  return "";
};

export const isInlinePlayableVideoSource = (src: string): boolean => {
  if (!src) return false;
  const lowerSrc = src.toLowerCase();
  if (lowerSrc.startsWith("blob:") || lowerSrc.startsWith("data:video/")) {
    return true;
  }

  try {
    const url = new URL(src, window.location.origin);
    if (isObjectStorageUrl(url)) {
      return true;
    }

    const extension = url.pathname.split(".").pop()?.toLowerCase() || "";
    return inlinePlayableVideoExtensions.includes(extension);
  } catch {
    const extension = lowerSrc.split("?")[0].split("#")[0].split(".").pop() || "";
    return inlinePlayableVideoExtensions.includes(extension);
  }
};

const normalizeMediaWidth = (
  width: unknown,
  mediaType: unknown,
): string | number => {
  if (
    mediaType === "image" &&
    (!width || width === "100%" || width === "auto")
  ) {
    return DEFAULT_IMAGE_WIDTH;
  }
  if (
    mediaType === "document" &&
    (!width || width === "100%" || width === "auto")
  ) {
    return DEFAULT_DOCUMENT_WIDTH;
  }
  if (typeof width === "number") return width;
  return typeof width === "string" && width.trim() ? width : "100%";
};

// Helper to detect media type from URL or extension
export const detectMediaType = (
  src: string
): "image" | "video" | "document" | "unknown" => {
  if (!src) return "unknown";

  const lowerSrc = src.toLowerCase();

  // Check for data URLs
  if (lowerSrc.startsWith("data:image/")) return "image";
  if (lowerSrc.startsWith("data:video/")) return "video";
  if (lowerSrc.startsWith("data:application/")) return "document";

  // Extract extension from URL
  try {
    const url = new URL(src, window.location.origin);
    if (isKnownVideoUrl(url)) {
      return "video";
    }

    const pathname = url.pathname.toLowerCase();
    const ext = pathname.split(".").pop() || "";

    if (imageExtensions.includes(ext)) {
      return "image";
    }
    if (videoExtensions.includes(ext)) {
      return "video";
    }
    if (documentExtensions.includes(ext)) {
      return "document";
    }
  } catch {
    // URL parsing failed, try simple extension check
    const ext = src.split(".").pop()?.toLowerCase() || "";
    if (imageExtensions.includes(ext)) {
      return "image";
    }
    if (videoExtensions.includes(ext)) {
      return "video";
    }
    if (documentExtensions.includes(ext)) {
      return "document";
    }
  }

  return "unknown";
};

export const resolveMediaType = (
  mediaType: unknown,
  src: unknown,
): "image" | "video" | "document" => {
  const explicitType =
    mediaType === "image" || mediaType === "video" || mediaType === "document"
      ? mediaType
      : null;
  const detectedType = detectMediaType(String(src || ""));

  if (
    detectedType !== "unknown" &&
    (!explicitType || explicitType === "image" || explicitType === "document")
  ) {
    return detectedType;
  }

  return explicitType || "image";
};

const resolveLegacyMediaType = (
  mediaType: unknown,
  src: unknown,
  nameHint?: unknown,
): "image" | "video" | "document" => {
  const resolvedType = resolveMediaType(mediaType, src);
  if (resolvedType !== "image") return resolvedType;

  const hintType = detectMediaType(String(nameHint || ""));
  return hintType === "video" || hintType === "document" ? hintType : resolvedType;
};

// Get file name from URL
export const getFileNameFromUrl = (src: string): string => {
  if (!src) return "File";

  const getLastPathSegment = (value: string): string => {
    const normalizedSegments = decodeURIComponent(value)
      .split("/")
      .filter(Boolean);
    return normalizedSegments[normalizedSegments.length - 1] || "File";
  };

  try {
    const url = new URL(src, window.location.origin);
    const pathname = url.pathname;
    const segments = pathname.split("/");
    const lastSegment = segments[segments.length - 1];
    if (lastSegment) {
      return getLastPathSegment(lastSegment);
    }
  } catch {
    // Try simple extraction
    const match = src.match(/[^/]+\.[a-zA-Z0-9]+(?:\?|$)/);
    if (match) {
      return getLastPathSegment(match[0].replace(/\?.*$/, ""));
    }
  }

  return "File";
};

export interface ResizableMediaOptions {
  HTMLAttributes: Record<string, unknown>;
  uploadFn?: (file: File) => Promise<MediaUploadResult>;
  component?: React.ComponentType<NodeViewProps>;
  onMediaDelete?: (src: string) => void;
}

const collectTrackedMediaUrls = (
  node: {
    attrs?: {
      src?: unknown;
      thumbnailUrl?: unknown;
    };
  },
  target: Set<string>,
) => {
  const src =
    typeof node.attrs?.src === "string" ? node.attrs.src.trim() : "";
  const thumbnailUrl =
    typeof node.attrs?.thumbnailUrl === "string"
      ? node.attrs.thumbnailUrl.trim()
      : "";

  if (src) target.add(src);
  if (thumbnailUrl) target.add(thumbnailUrl);
};

export const ResizableMedia = Node.create<ResizableMediaOptions>({
  name: "resizableMedia",

  addOptions() {
    return {
      HTMLAttributes: {},
      uploadFn: undefined,
    };
  },

  group: "block",

  draggable: true,

  atom: true,

  addAttributes() {
    // Helper: find nested img or video inside a wrapper div
    const getNestedMedia = (element: HTMLElement): HTMLElement | null =>
      element.querySelector("img") || element.querySelector("video");
    const getMediaSource = (element: HTMLElement): string | null => {
      const direct =
        element.getAttribute("src") || element.getAttribute("data-src");
      if (direct) return direct;
      const nestedMediaSource = getNestedMedia(element)?.getAttribute("src");
      if (nestedMediaSource) return nestedMediaSource;
      return element.querySelector("a[href]")?.getAttribute("href") || null;
    };

    return {
      src: {
        default: null,
        parseHTML: (element) => getMediaSource(element),
        renderHTML: (attributes) => ({
          src: attributes.src,
        }),
      },
      alt: {
        default: null,
        parseHTML: (element) => {
          const direct = element.getAttribute("alt");
          if (direct) return direct;
          return getNestedMedia(element)?.getAttribute("alt") || null;
        },
        renderHTML: (attributes) => ({
          alt: attributes.alt,
        }),
      },
      title: {
        default: null,
        parseHTML: (element) => {
          const direct = element.getAttribute("title");
          if (direct) return direct;
          return getNestedMedia(element)?.getAttribute("title") || null;
        },
        renderHTML: (attributes) => ({
          title: attributes.title,
        }),
      },
      thumbnailUrl: {
        default: null,
        parseHTML: (element) => {
          const direct = element.getAttribute("data-thumbnail-url");
          if (direct) return direct;
          return element.querySelector("img.video-thumbnail-image")?.getAttribute("src") || null;
        },
        renderHTML: (attributes) => ({
          "data-thumbnail-url": attributes.thumbnailUrl,
        }),
      },
      width: {
        default: DEFAULT_IMAGE_WIDTH,
        parseHTML: (element) =>
          element.getAttribute("width") || element.style.width || DEFAULT_IMAGE_WIDTH,
        renderHTML: (attributes) => ({
          width: normalizeMediaWidth(attributes.width, attributes.mediaType),
        }),
      },
      height: {
        default: "auto",
        parseHTML: (element) => {
          const direct = element.getAttribute("height") || element.style.height;
          if (direct) return direct;
          const media = getNestedMedia(element);
          return media?.style.height || media?.getAttribute("height") || "auto";
        },
        renderHTML: (attributes) => ({
          height: attributes.height,
        }),
      },
      mediaType: {
        default: "image",
        parseHTML: (element) => {
          const mediaSrc = getMediaSource(element) || "";
          const dataType = element.getAttribute("data-media-type");
          const media = getNestedMedia(element);
          const nameHint =
            media?.getAttribute("alt") || media?.getAttribute("title") || "";
          if (dataType) return resolveLegacyMediaType(dataType, mediaSrc, nameHint);
          // Prioritize URL-based detection over element type, because legacy HTML
          // may wrap video/document URLs in an <img> tag
          const detected = detectMediaType(mediaSrc);
          if (detected !== "unknown") return detected;
          if (element.querySelector("a[href]")) return "document";
          // Fallback to element type
          if (element.querySelector("video")) return "video";
          if (element.querySelector("img")) return "image";
          return "image";
        },
        renderHTML: (attributes) => ({
          "data-media-type": attributes.mediaType,
        }),
      },
      alignment: {
        default: "center",
        parseHTML: (element) => {
          const dataAttr = element.getAttribute("data-alignment");
          if (dataAttr) return dataAttr;
          // Fallback: extract from class name (e.g. "resizable-media-left")
          const className = element.getAttribute("class") || "";
          if (className.includes("resizable-media-left")) return "left";
          if (className.includes("resizable-media-right")) return "right";
          return "center";
        },
        renderHTML: (attributes) => ({
          "data-alignment": attributes.alignment,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="resizable-media"]',
        getAttrs: (element) => {
          const el = element as HTMLElement;
          const img = el.querySelector("img");
          const video = el.querySelector("video");
          const anchor = el.querySelector("a[href]");
          const media = img || video;
          const src =
            el.getAttribute("data-src") ||
            media?.getAttribute("src") ||
            anchor?.getAttribute("href") ||
            null;
          return {
            src,
            thumbnailUrl: el.getAttribute("data-thumbnail-url") || null,
            alt: media?.getAttribute("alt") || null,
            title: media?.getAttribute("title") || null,
            mediaType: (() => {
              const dataType = el.getAttribute("data-media-type");
              const nameHint =
                media?.getAttribute("alt") || media?.getAttribute("title") || "";
              if (dataType) return resolveLegacyMediaType(dataType, src, nameHint);
              const mediaSrc = src || "";
              const detected = detectMediaType(mediaSrc);
              return detected !== "unknown"
                ? detected
                : anchor
                  ? "document"
                  : video
                    ? "video"
                    : "image";
            })(),
            alignment: el.getAttribute("data-alignment") || "center",
            width: el.getAttribute("width") || el.style.width || "100%",
            height: media?.style.height || media?.getAttribute("height") || "auto",
          };
        },
      },
      {
        tag: "a[href].document-link",
        getAttrs: (element) => {
          const anchor = element as HTMLAnchorElement;
          const src = anchor.getAttribute("href");
          if (!src) return false;
          const wrapper = anchor.closest<HTMLElement>(".document-attachment");
          return {
            src,
            alt:
              anchor.querySelector(".document-name")?.textContent?.trim() ||
              anchor.textContent?.trim() ||
              getFileNameFromUrl(src),
            mediaType: "document",
            alignment: wrapper
              ? wrapper.classList.contains("resizable-media-left")
                ? "left"
                : wrapper.classList.contains("resizable-media-right")
                  ? "right"
                  : "center"
              : "center",
            width: wrapper?.getAttribute("width") || wrapper?.style.width || DEFAULT_DOCUMENT_WIDTH,
            height: "auto",
          };
        },
      },
      {
        tag: "img[src]",
        getAttrs: (element) => {
          const src = (element as HTMLElement).getAttribute("src");
          if (!src) return false;
          return {
            src,
            mediaType: "image",
          };
        },
      },
      {
        tag: "video[src]",
        getAttrs: (element) => {
          const src = (element as HTMLElement).getAttribute("src");
          if (!src) return false;
          return {
            src,
            mediaType: "video",
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { src, alt, title, thumbnailUrl, width, height, alignment } = HTMLAttributes;
    const mediaType = resolveMediaType(HTMLAttributes.mediaType, src);
    const normalizedWidth = normalizeMediaWidth(width, mediaType);

    const wrapperAttrs = {
      "data-type": "resizable-media",
      "data-src": src,
      "data-media-type": mediaType,
      "data-thumbnail-url": thumbnailUrl,
      "data-alignment": alignment,
      class: `resizable-media-wrapper resizable-media-${alignment}`,
      width: normalizedWidth,
      style: `width: ${
        typeof normalizedWidth === "number" ? `${normalizedWidth}px` : normalizedWidth
      }; max-width: 100%;`,
    };

    if (mediaType === "video" && isInlinePlayableVideoSource(String(src || ""))) {
      return [
        "div",
        wrapperAttrs,
        [
          "video",
          mergeAttributes(this.options.HTMLAttributes, {
            src,
            title,
            controls: true,
            playsinline: true,
            preload: "metadata",
            style: `width: 100%; height: ${typeof height === "number" ? `${height}px` : height}`,
          }),
        ],
      ];
    }

    if (mediaType === "video") {
      const fileName = getFileNameFromUrl(src);
      const videoThumbnailUrl =
        String(thumbnailUrl || "") || getVideoThumbnailForUrl(String(src || ""));

      if (videoThumbnailUrl) {
        return [
          "div",
          wrapperAttrs,
          [
            "a",
            {
              href: src,
              target: "_blank",
              rel: "noopener noreferrer",
              class: "video-thumbnail-link",
            },
            [
              "img",
              {
                src: videoThumbnailUrl,
                alt: alt || title || fileName,
                class: "video-thumbnail-image",
                style: "width: 100%; height: auto;",
              },
            ],
            ["span", { class: "video-play-overlay" }, "▶"],
          ],
        ];
      }

      return [
        "div",
        mergeAttributes(wrapperAttrs, { class: `${wrapperAttrs.class} video-attachment` }),
        [
          "a",
          {
            href: src,
            target: "_blank",
            rel: "noopener noreferrer",
            class: "video-preview-link",
          },
          [
            "span",
            { class: "video-preview-icon" },
            "▶",
          ],
          [
            "span",
            { class: "video-preview-name" },
            title || fileName,
          ],
        ],
      ];
    }

    if (mediaType === "document") {
      const fileName = String(alt || title || getFileNameFromUrl(src));
      const ext = fileName.split(".").pop()?.toLowerCase() || "";

      return [
        "div",
        mergeAttributes(wrapperAttrs, { class: `${wrapperAttrs.class} document-attachment` }),
        [
          "a",
          {
            href: src,
            target: "_blank",
            rel: "noopener noreferrer",
            class: "document-link",
          },
          [
            "span",
            { class: `document-icon document-icon-${ext}` },
          ],
          [
            "span",
            { class: "document-name" },
            fileName,
          ],
        ],
      ];
    }

    // Default: image
    return [
      "div",
      wrapperAttrs,
      [
        "img",
        mergeAttributes(this.options.HTMLAttributes, {
          src,
          alt,
          title,
          style: `width: 100%; height: ${typeof height === "number" ? `${height}px` : height}`,
        }),
      ],
    ];
  },

  addCommands() {
    return {
      setMedia:
        (options) =>
        ({ commands }) => {
          let mediaType = options.mediaType || detectMediaType(options.src);
          if (mediaType === "unknown" && (options.alt || options.title)) {
            mediaType = detectMediaType(options.alt || options.title || "");
          }
          const resolvedMediaType = mediaType === "unknown" ? "image" : mediaType;
          return commands.insertContent({
            type: this.name,
            attrs: {
              ...options,
              mediaType: resolvedMediaType,
              width: normalizeMediaWidth(options.width, resolvedMediaType),
            },
          });
        },
    };
  },

  addNodeView() {
    // Dynamic import to avoid circular dependencies
    // The ResizableMediaComponent will be passed via configure()
    return ReactNodeViewRenderer(
      // This will be replaced by configure() with the actual component
      this.options.component || (() => null)
    );
  },

  addProseMirrorPlugins() {
    const onMediaDelete = this.options.onMediaDelete;
    const nodeTypeName = this.name;

    return [
      // Plugin to track media node deletions (backspace, delete, cut, etc.)
      ...(onMediaDelete
        ? [
            new Plugin({
              key: new PluginKey("resizableMediaDeletionTracker"),
              appendTransaction(transactions, oldState, newState) {
                // Only process if any transaction changed the doc
                const hasDocChange = transactions.some(tr => tr.docChanged);
                if (!hasDocChange) return null;

                // Skip internal media updates (e.g. blob→storage URL replacement)
                const isInternalMediaUpdate = transactions.some(tr => tr.getMeta("skipMediaDeleteTracking"));
                if (isInternalMediaUpdate) return null;

                // Collect all media src URLs in old and new documents
                const oldSrcs = new Set<string>();
                const newSrcs = new Set<string>();

                oldState.doc.descendants((node) => {
                  if (node.type.name === nodeTypeName) {
                    collectTrackedMediaUrls(node, oldSrcs);
                  }
                });

                newState.doc.descendants((node) => {
                  if (node.type.name === nodeTypeName) {
                    collectTrackedMediaUrls(node, newSrcs);
                  }
                });

                // Only call onMediaDelete for src URLs present in old but NOT in new
                for (const src of oldSrcs) {
                  if (!newSrcs.has(src)) {
                    onMediaDelete(src);
                  }
                }

                return null;
              },
            }),
          ]
        : []),
      new Plugin({
        key: new PluginKey("resizableMediaDropHandler"),
        props: {
          handleDrop: (view, event, _slice, moved) => {
            if (moved || !event.dataTransfer?.files?.length) {
              return false;
            }

            const file = event.dataTransfer.files[0];
            if (!file) return false;

            const ext = file.name.split(".").pop()?.toLowerCase() || "";
            const isImage = imageExtensions.includes(ext);
            const isVideo = videoExtensions.includes(ext);
            const isDocument = documentExtensions.includes(ext);

            if (!isImage && !isVideo && !isDocument) {
              return false;
            }

            event.preventDefault();

            const uploadFn = this.options.uploadFn;
            if (uploadFn) {
              uploadFn(file).then((src) => {
                const { tr } = view.state;
                const pos = view.posAtCoords({
                  left: event.clientX,
                  top: event.clientY,
                });

                if (pos) {
                  const mediaType = isImage ? "image" : isVideo ? "video" : "document";
                  const node = view.state.schema.nodes.resizableMedia.create({
                    src: typeof src === "string" ? src : src.url,
                    alt: file.name,
                    thumbnailUrl:
                      typeof src === "string" ? undefined : src.thumbnailUrl,
                    mediaType,
                  });
                  const transaction = tr.insert(pos.pos, node);
                  view.dispatch(transaction);
                }
              });
            } else {
              // Fallback to base64
              const reader = new FileReader();
              reader.onload = () => {
                const src = reader.result as string;
                const { tr } = view.state;
                const pos = view.posAtCoords({
                  left: event.clientX,
                  top: event.clientY,
                });

                if (pos) {
                  const mediaType = isImage ? "image" : isVideo ? "video" : "document";
                  const node = view.state.schema.nodes.resizableMedia.create({
                    src,
                    alt: file.name,
                    mediaType,
                  });
                  const transaction = tr.insert(pos.pos, node);
                  view.dispatch(transaction);
                }
              };
              reader.readAsDataURL(file);
            }

            return true;
          },
        },
      }),
    ];
  },
});

export default ResizableMedia;
